import styled, { css } from 'styled-components';
import { TypographyStyles, colours, focusOutline, fontWeights, fonts, media, spacing } from '../../stylings';
import { Skeleton } from '../skeleton';

export const ProductLink = styled(Skeleton)`
  aspect-ratio: 1/1;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  position: relative;
  text-decoration: none;
`;
export const ImageContainer = styled.div<{ $isVisible: boolean }>`
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  position: absolute;
  transition: opacity 0.5s ease-in-out;

  @media screen and (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const ProductNameMultiImage = css`
  ${TypographyStyles.Headings.H4SemiBold}
  font-size: 18px;
  height: 18px;
  margin: ${spacing.XXS} 0;
  text-transform: uppercase;

  @media ${media.greaterThan('lg')} {
    height: 48px;
  }
`;

const ProductNameSingleImage = css`
  ${fonts.Montserrat}
  ${fontWeights.SemiBold600}
  display: block;
  height: unset;
  line-height: 24px;
  margin: ${spacing.XXXS} 0 0;
  min-height: 24px;
  text-transform: capitalize;

  @media ${media.greaterThan('sm')} {
    font-size: 16px;
  }

  @media ${media.greaterThan('lg')} {
    font-size: 18px;
    height: unset;
  }
`;

export const ProductName = styled(Skeleton)<{ $isMultiImageName?: boolean }>`
  ${ProductNameMultiImage}
  ${({ $isMultiImageName }) => !$isMultiImageName && ProductNameSingleImage}

  &.truncated {
    -webkit-box-orient: vertical;
    /* stylelint-disable value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: ${({ $isMultiImageName }) => ($isMultiImageName ? 'nowrap' : 'unset')};
  }

  @media ${media.greaterThan('lg')} {
    ${({ $isMultiImageName }) => ($isMultiImageName ? '48px' : 'unset')}
    margin: ${spacing.XS} 0;
    width: 100%;
    &.truncated {
      white-space: unset;
    }
  }
`;
const ProductDetailsMultiImage = css`
  ${TypographyStyles.Body.Tiny.Regular}
  margin: ${spacing.XXS} 0;
  min-height: 22px;

  @media ${media.greaterThan('lg')} {
    ${TypographyStyles.Body.Small.Regular}
  }
`;
const ProductDetailsSingleImage = css`
  ${fonts.Montserrat}
  ${fontWeights.Regular400}
  font-size: 14px;
  line-height: 24px;
  min-height: 26px;

  @media ${media.greaterThan('sm')} {
    font-size: 12px;
    line-height: 20px;
  }

  @media ${media.greaterThan('lg')} {
    font-size: 14px;
    line-height: 24px;
  }
`;
export const ProductDetails = styled.div<{ $isMultiImageCategory?: boolean }>`
  display: grid;
  min-height: ${({ $isMultiImageCategory }) => ($isMultiImageCategory ? '36px' : 'unset')};

  @media ${media.greaterThan('lg')} {
    min-height: ${({ $isMultiImageCategory }) => ($isMultiImageCategory ? '44px' : 'unset')};
  }

  & * {
    ${ProductDetailsMultiImage}
    ${({ $isMultiImageCategory }) => !$isMultiImageCategory && ProductDetailsSingleImage}
    margin: 0;
    min-height: ${({ $isMultiImageCategory }) => ($isMultiImageCategory ? '18px' : 'unset')};
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: capitalize;
    white-space: nowrap;
  }

  & *:last-child {
    place-self: end stretch;
  }
`;
export const PriceContainer = styled.div`
  min-height: 28px;
`;

const ProductItemMainInfoDesktopWidth = css`
  @media ${media.greaterThan('lg')} {
    max-width: 259px;
    width: 50%;
  }
`;
export const ProductItemMainInfo = styled(Skeleton)`
  color: inherit;
  cursor: pointer;
  text-decoration: none;

  ${ProductItemMainInfoDesktopWidth}
`;

export const MultiImagePriceAndPromotionContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${ProductItemMainInfoDesktopWidth}
`;

export const SelectedThumbnailBorder = styled.div<{
  $isSelected: boolean;
  $isProductColour?: boolean;
}>`
  background-color: ${colours.TURQUOISE};
  bottom: 0;
  height: ${({ $isProductColour }) => ($isProductColour ? '4px' : '6px')};
  opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0)};
  pointer-events: none;
  position: absolute;
  top: ${({ $isProductColour }) => ($isProductColour ? '85%' : 'unset')};
  transition: opacity 0.5s ease-in-out;
  width: 100%;

  @media screen and (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media ${media.greaterThan('lg')} {
    height: ${({ $isProductColour }) => ($isProductColour ? '4px' : '9px')};
    top: ${({ $isProductColour }) => $isProductColour && '90%'};
  }
`;
export const ProductColourSelectorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
  margin-top: ${spacing.XXXS};

  @media ${media.greaterThan('lg')} {
    gap: ${spacing.XS};
    max-width: 261px;
    width: 50%;
  }
`;
export const ProductColourSelector = styled.div`
  align-items: center;
  display: flex;
  gap: ${spacing.XXXS};
  min-height: ${spacing.L};
`;
export const ProductColour = styled.a<{ $isSelectedColorLink: boolean }>`
  border: ${({ $isSelectedColorLink }) => $isSelectedColorLink && `1px solid ${colours.BLACK}`};
  cursor: pointer;
  height: 32px;
  position: relative;
  width: 32px;

  @media ${media.greaterThan('lg')} {
    height: 40px;
    width: 40px;
  }
`;
export const ProductShopNowLink = styled(Skeleton)`
  background-color: ${colours.BLACK};
  border: 1px solid ${colours.BLACK};
  color: ${colours.WHITE};
  cursor: pointer;
  ${TypographyStyles.Button.MontserratSemiBold}
  letter-spacing: 0px;
  min-height: 48px;
  padding: 14px;
  text-align: center;
  text-decoration: none;
  width: 100%;

  &:focus-visible {
    ${focusOutline}
  }
  &:hover {
    background-color: ${colours.WHITE};
    color: ${colours.BLACK};
  }
  &:focus {
    /* Empty focus state to satisfy linter */
  }

  @media ${media.greaterThan('lg')} {
    min-height: 50px;
  }
`;
export const ProductMoreColorsSingleAndMultiImage = css`
  ${TypographyStyles.Body.Tiny.SemiBold}
  color: ${colours.WHITE};
  cursor: pointer;
  letter-spacing: 0px;
  margin: 0;
  padding: 0;
  text-decoration: none;
  text-transform: uppercase;
`;
export const ImageAndBadgeContainer = styled.div`
  position: relative;
`;
